import { cn } from '@/lib/utils';
import { Circle, Loader2, AlertCircle } from 'lucide-react';

export type Status =
  | 'healthy'
  | 'unhealthy'
  | 'unknown'
  | 'running'
  | 'starting'
  | 'stopping'
  | 'stopped'
  | 'paused'
  | 'suspended'
  | 'offline'
  | 'online';

interface StatusBadgeProps {
  status: Status;
  label?: string;
  className?: string;
  showIcon?: boolean;
}

const STYLES: Record<Status, { dot: string; text: string; bg: string }> = {
  healthy: {
    dot: 'text-emerald-500 fill-emerald-500',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/30',
  },
  running: {
    dot: 'text-emerald-500 fill-emerald-500',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/30',
  },
  online: {
    dot: 'text-emerald-500 fill-emerald-500',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/30',
  },
  unhealthy: {
    dot: 'text-rose-500 fill-rose-500',
    text: 'text-rose-400',
    bg: 'bg-rose-500/10 border-rose-500/30',
  },
  stopped: {
    dot: 'text-zinc-500 fill-zinc-500',
    text: 'text-zinc-400',
    bg: 'bg-zinc-500/10 border-zinc-500/30',
  },
  starting: {
    dot: 'text-sky-500 fill-sky-500',
    text: 'text-sky-400',
    bg: 'bg-sky-500/10 border-sky-500/30',
  },
  stopping: {
    dot: 'text-amber-500 fill-amber-500',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/30',
  },
  suspended: {
    dot: 'text-amber-500 fill-amber-500',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/30',
  },
  paused: {
    dot: 'text-amber-500 fill-amber-500',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/30',
  },
  unknown: {
    dot: 'text-zinc-500 fill-zinc-500',
    text: 'text-zinc-400',
    bg: 'bg-zinc-500/10 border-zinc-500/30',
  },
  offline: {
    dot: 'text-rose-500 fill-rose-500',
    text: 'text-rose-400',
    bg: 'bg-rose-500/10 border-rose-500/30',
  },
};

const LABELS: Record<Status, string> = {
  healthy: 'healthy',
  running: 'running',
  online: 'online',
  unhealthy: 'down',
  stopped: 'stopped',
  starting: 'starting',
  stopping: 'stopping',
  suspended: 'suspended',
  paused: 'paused',
  unknown: 'unknown',
  offline: 'offline',
};

export function StatusBadge({ status, label, className, showIcon = true }: StatusBadgeProps) {
  const s = STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
        s.bg,
        s.text,
        className,
      )}
    >
      {showIcon && <Circle className={cn('h-2 w-2', s.dot)} />}
      {label ?? LABELS[status]}
    </span>
  );
}

export function StatusIcon({ status, className }: { status: Status; className?: string }) {
  if (status === 'healthy' || status === 'running' || status === 'online') {
    return <Circle className={cn('h-2.5 w-2.5 fill-emerald-500 text-emerald-500', className)} />;
  }
  if (status === 'unknown' || status === 'stopped' || status === 'suspended' || status === 'paused' || status === 'offline') {
    return <Loader2 className={cn('h-3.5 w-3.5 text-zinc-500', className)} />;
  }
  return <AlertCircle className={cn('h-3.5 w-3.5 text-rose-500', className)} />;
}
